import { useEffect, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Star, StarHalf, Trophy, PencilSimple, Check, Plus, Bell, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function Stars({ value, size = 16 }) {
  if (value == null) return <span className="text-muted-foreground text-sm">Keine Daten</span>;
  const full = Math.floor(value);
  const half = value - full >= 0.25 && value - full < 0.75;
  const extra = value - full >= 0.75 ? 1 : 0;
  const total = full + extra;
  return (
    <div className="flex items-center gap-0.5">
      {[0,1,2,3,4].map(i => (
        i < total ? <Star key={i} size={size} weight="fill" className="text-amber-500"/> :
        (i === total && half ? <StarHalf key={i} size={size} weight="fill" className="text-amber-500"/> :
        <Star key={i} size={size} className="text-slate-300"/>)
      ))}
    </div>
  );
}

const PLATFORM_COLORS = {
  "Booking.com": "#003580",
  "Google": "#4285F4",
  "Airbnb": "#FF5A5F",
  "Tripadvisor": "#00AA6C",
};

export default function Reviews() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [notif, setNotif] = useState({ items: [], unread: 0 });
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [form, setForm] = useState({
    platform: "Google", author: "", rating: "", scale: 5, title: "", text: "", review_date: ""
  });

  const loadAll = () => {
    api.get("/reviews").then(r => setData(r.data));
    api.get("/review-notifications").then(r => setNotif(r.data));
  };
  useEffect(() => { loadAll(); }, []);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/reviews/sync-now");
      const added = data.results.reduce((n,r)=>n+(r.delta||0),0);
      if (added > 0) toast.success(`${added} neue Bewertung(en) gefunden — bitte oben ansehen`);
      else toast.success("Prüfung abgeschlossen · keine neuen Bewertungen");
      loadAll();
    } catch { toast.error("Automatische Prüfung fehlgeschlagen"); }
    finally { setSyncing(false); }
  };

  if (!data) return null;

  const savePlatform = async (r) => {
    const v = editVal === "" ? null : parseFloat(editVal.replace(",", "."));
    try {
      await api.put(`/reviews/${r.id}`, { rating: v });
      toast.success("Bewertung aktualisiert"); setEditId(null); loadAll();
    } catch { toast.error("Fehler"); }
  };

  const submitNotif = async () => {
    if (!form.text.trim()) { toast.error("Bitte Bewertungstext eingeben"); return; }
    try {
      await api.post("/review-notifications", {
        ...form,
        rating: form.rating === "" ? null : parseFloat(String(form.rating).replace(",", ".")),
        scale: form.scale || 5,
      });
      toast.success("Neue Bewertung erfasst");
      setDialogOpen(false);
      setForm({ platform: "Google", author: "", rating: "", scale: 5, title: "", text: "", review_date: "" });
      loadAll();
    } catch { toast.error("Fehler"); }
  };

  const markRead = async (id) => {
    try { await api.patch(`/review-notifications/${id}/read`); loadAll(); }
    catch { toast.error("Fehler"); }
  };
  const removeNotif = async (id) => {
    try { await api.delete(`/review-notifications/${id}`); loadAll(); }
    catch { toast.error("Fehler"); }
  };

  return (
    <div className="space-y-6" data-testid="reviews-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>Bewertungen</h1>
          <p className="text-muted-foreground mt-1">
            Ø <strong>{data.average_stars != null ? data.average_stars.toFixed(1) : "—"} / 5</strong> über {data.platforms_with_data} Plattform(en)
            {notif.unread > 0 && <span className="ml-2 inline-flex items-center gap-1 text-amber-700"><Bell size={14} weight="fill"/> {notif.unread} neu</span>}
            <span className="ml-2 text-xs">· Google &amp; Booking werden am 1. + 15. jedes Monats automatisch geprüft</span>
          </p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={syncNow} disabled={syncing} className="rounded-xl" data-testid="sync-now-btn">
              {syncing ? "Prüfe…" : "Jetzt prüfen"}
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-xl bg-primary" data-testid="new-review-btn"><Plus size={16} weight="bold"/> <span className="ml-1.5">Neue Bewertung erfassen</span></Button>
              </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Neue Bewertung notieren</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Plattform</Label>
                  <Select value={form.platform} onValueChange={(v)=>setForm({...form, platform: v, scale: v === "Booking.com" ? 10 : 5})}>
                    <SelectTrigger className="rounded-xl mt-1.5" data-testid="notif-platform"><SelectValue/></SelectTrigger>
                    <SelectContent>{["Booking.com","Google","Airbnb","Tripadvisor","Sonstige"].map(p=><SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Autor / Gast</Label><Input value={form.author} onChange={e=>setForm({...form, author: e.target.value})} className="rounded-xl mt-1.5" data-testid="notif-author"/></div>
                  <div><Label>Datum</Label><Input type="date" value={form.review_date} onChange={e=>setForm({...form, review_date: e.target.value})} className="rounded-xl mt-1.5"/></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Bewertung</Label><Input value={form.rating} onChange={e=>setForm({...form, rating: e.target.value})} placeholder={`0-${form.scale}`} className="rounded-xl mt-1.5" data-testid="notif-rating"/></div>
                  <div><Label>Skala</Label><Input type="number" value={form.scale} onChange={e=>setForm({...form, scale: +e.target.value})} className="rounded-xl mt-1.5"/></div>
                </div>
                <div><Label>Titel (optional)</Label><Input value={form.title} onChange={e=>setForm({...form, title: e.target.value})} className="rounded-xl mt-1.5"/></div>
                <div><Label>Bewertungstext</Label><Textarea rows={4} value={form.text} onChange={e=>setForm({...form, text: e.target.value})} className="rounded-xl mt-1.5" data-testid="notif-text"/></div>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={()=>setDialogOpen(false)} className="rounded-xl">Abbrechen</Button>
                <Button onClick={submitNotif} className="rounded-xl bg-primary" data-testid="save-review-notification">Speichern</Button>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {/* Notifications - Neue Bewertungen zum Abhaken */}
      <div>
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{fontFamily:"'Work Sans',sans-serif"}}>
          <Bell size={18} weight="duotone"/> Neue Bewertungen
          {notif.unread > 0 && <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">{notif.unread}</span>}
        </h2>
        {notif.items.filter(n => !n.read).length === 0 && (
          <div className="msb-card text-sm text-muted-foreground" data-testid="no-new-reviews">
            Aktuell keine neuen Bewertungen zum Bearbeiten. 
          </div>
        )}
        <div className="space-y-3">
          {notif.items.filter(n => !n.read).map(n => (
            <div key={n.id} className="msb-card" data-testid={`review-notif-${n.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold" style={{color: PLATFORM_COLORS[n.platform] || "#374151"}}>
                      {n.platform}
                    </span>
                    {n.rating != null && (
                      <>
                        <span className="text-sm font-bold">{String(n.rating).replace(".", ",")} / {n.scale}</span>
                        <Stars value={n.rating * 5 / (n.scale || 5)} size={14}/>
                      </>
                    )}
                    <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">NEU</span>
                  </div>
                  <div className="mt-2 text-sm text-muted-foreground">
                    {n.author && <span className="font-medium text-foreground">{n.author}</span>}
                    {n.review_date && <span className="ml-2">· {formatDate(n.review_date)}</span>}
                  </div>
                  {n.title && <div className="mt-2 font-medium">{n.title}</div>}
                  <p className="mt-2 whitespace-pre-wrap text-sm">{n.text}</p>
                </div>
                <Button variant="outline" size="sm" onClick={()=>markRead(n.id)} className="rounded-xl shrink-0" data-testid={`mark-read-${n.id}`}>
                  <Check size={14} weight="bold"/> <span className="ml-1">Gelesen</span>
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Platform overview */}
      <div>
        <h2 className="text-lg font-semibold mb-3" style={{fontFamily:"'Work Sans',sans-serif"}}>Plattform-Bewertungen</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.reviews.map(r => {
            const stars5 = r.rating != null ? (r.rating * 5 / r.scale) : null;
            return (
              <a key={r.id} href={r.url} target="_blank" rel="noreferrer"
                 className="msb-card block hover:no-underline" data-testid={`platform-${r.platform}`}>
                <div className="flex items-center justify-between">
                  <div className="font-semibold" style={{color: r.color, fontFamily:"'Work Sans',sans-serif"}}>{r.platform}</div>
                  {isAdmin && editId !== r.id && (
                    <button onClick={(e)=>{e.preventDefault(); setEditId(r.id); setEditVal(r.rating != null ? String(r.rating) : "");}} className="text-muted-foreground hover:text-primary" data-testid={`edit-platform-${r.platform}`}>
                      <PencilSimple size={14}/>
                    </button>
                  )}
                </div>
                {editId === r.id ? (
                  <div className="mt-2 flex gap-1" onClick={(e)=>e.preventDefault()}>
                    <Input value={editVal} onChange={e=>setEditVal(e.target.value)} placeholder={`0-${r.scale}`} className="h-8 rounded-lg text-sm"/>
                    <Button size="sm" className="h-8 rounded-lg bg-primary" onClick={(e)=>{e.preventDefault(); savePlatform(r);}}><Check size={14}/></Button>
                  </div>
                ) : (
                  <>
                    <div className="mt-2 flex items-baseline gap-1.5">
                      <div className="text-3xl font-bold text-foreground" style={{fontFamily:"'Work Sans',sans-serif"}}>
                        {r.rating != null ? String(r.rating).replace(".", ",") : "—"}
                      </div>
                      <div className="text-xs text-muted-foreground">/ {r.scale}</div>
                    </div>
                    <div className="mt-1"><Stars value={stars5}/></div>
                    <div className="mt-2 text-xs text-muted-foreground">
                      {r.count != null ? `${r.count} Bewertung${r.count === 1 ? "" : "en"}` : "Keine Daten"}
                    </div>
                    {r.badge && (
                      <div className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-amber-50 text-amber-800 px-2 py-0.5 rounded-full">
                        <Trophy size={10} weight="fill"/> {r.badge}
                      </div>
                    )}
                  </>
                )}
              </a>
            );
          })}
        </div>
      </div>

      {/* History - already read */}
      {notif.items.filter(n => n.read).length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-3" style={{fontFamily:"'Work Sans',sans-serif"}}>Verlauf</h2>
          <div className="msb-card p-0 overflow-hidden">
            {notif.items.filter(n => n.read).map(n => (
              <div key={n.id} className="p-4 border-b border-border last:border-0 flex items-start justify-between gap-3" data-testid={`review-history-${n.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-semibold" style={{color: PLATFORM_COLORS[n.platform] || "#374151"}}>{n.platform}</span>
                    {n.rating != null && <span>{String(n.rating).replace(".", ",")}/{n.scale}</span>}
                    {n.author && <span className="text-muted-foreground">· {n.author}</span>}
                    {n.review_date && <span className="text-muted-foreground">· {formatDate(n.review_date)}</span>}
                  </div>
                  <div className="mt-1 text-sm text-muted-foreground line-clamp-2">{n.text}</div>
                </div>
                <button onClick={()=>removeNotif(n.id)} className="text-xs text-muted-foreground hover:text-destructive shrink-0">Löschen</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
